import React from 'react'
import { Training, TrainingContent, VolunteerTrainingPageDetailsMetaProps } from '../data/types'
import { Card } from './ui/Card'
import { ClockIcon, DocumentIcon } from '../icons'

type Props = {
    training: Training
    totalDuration: number
    moduleCount: number
    meta: VolunteerTrainingPageDetailsMetaProps
}

export const TrainingDetailsHeader = (props: Props) => {
    const { training, totalDuration, moduleCount, meta } = props

    const completedModulesCount = training.completed_modules?.length || 0
    const progressPercentage = moduleCount > 0 ? Math.round((completedModulesCount / moduleCount) * 100) : 0

    return (
        <Card className="mb-8">
            <div className="relative p-8 bg-repeat bg-tile bg-[url('/public/header_tile_gold.png')]">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-900/50 to-primary-400/30"></div>
                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-4xl font-bold text-white">{training.name}</h1>
                        <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                            training.completion_status === 1
                                ? 'bg-success-100 dark:bg-success-900 text-success-700 dark:text-success-300'
                                : 'bg-warn-100 dark:bg-warn-900 text-warn-700 dark:text-warn-300'
                        }`}>
                            {training.completion_status === 1 ? meta.completedBadge : meta.inProgressBadge}
                        </span>
                    </div>
                    {training.description && (
                        <p className="text-primary-100 text-lg">{training.description}</p>
                    )}
                </div>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                    <DocumentIcon className="w-6 h-6 text-gray-500 dark:text-gray-400 me-3" />
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{meta.modulesLabel}</p>
                        <p className="text-gray-900 dark:text-gray-100">
                            {completedModulesCount} / {moduleCount} {moduleCount === 1 ? 'Module' : 'Modules'}
                        </p>
                    </div>
                </div>
                {totalDuration > 0 && (
                    <div className="flex items-center">
                        <ClockIcon className="w-6 h-6 text-gray-500 dark:text-gray-400 me-3" />
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{meta.totalDurationLabel}</p>
                            <p className="text-gray-900 dark:text-gray-100">
                                {totalDuration} {meta.minutesSuffix}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Progress Bar */}
            {moduleCount > 0 && (
                <div className="px-6 pb-6">
                    <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-600 dark:text-gray-400">Progress</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{progressPercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div
                            className="bg-success-600 h-2.5 rounded-full transition-all duration-300"
                            style={{ width: `${progressPercentage}%` }}
                        ></div>
                    </div>
                </div>
            )}
        </Card>
    )
}
